'use client';
import { useAddGCToBasket } from '@hooks/basket/useAddGCToBasket';
import { productViewed } from '@repo/ui/src/services/analytics';
import { gcToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { track } from '@repo/ui/src/services/analytics/utils';
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import { Container, Form, FormError } from './styles';
import Typography from '@mui/material/Typography';
import createCurrencyString from '@repo/ui/src/functions/currency-functions';
import BaseButtonWithProgress from '@components/BaseButtonWithProgress';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

interface FieldProps {
  label: string;
  errorMessage: string;
}

interface SelectFieldProps extends FieldProps {
  options: (string | number)[];
  customAmountLabel?: string;
  customAmountMin?: number;
  customAmountMax?: number;
  includeCustomAmountOption: boolean;
}

interface FormProps {
  amount: SelectFieldProps;
  customAmount: FieldProps;
  senderName: FieldProps;
  recipientName: FieldProps;
  recipientEmail: FieldProps;
  message: FieldProps;
}

interface Props {
  title?: string;
  description?: string;
  buttonText?: string;
  form?: FormProps;
  formSubmissionErrorText?: string;
}

const formTypeOverrides = {
  customAmount: 'number',
  recipientEmail: 'email',
};

const orderedFormKeys = [
  'amount',
  'customAmount',
  'senderName',
  'recipientName',
  'recipientEmail',
  'message',
];

const GiftCardDetail = ({
  title,
  description,
  buttonText,
  form,
  formSubmissionErrorText,
}: Props) => {
  const { currency, locale } = useCurrentUrl();
  const { trigger } = useAddGCToBasket();
  const [submissionError, setSubmissionError] = React.useState('');

  React.useEffect(() => {
    track(productViewed, {
      ...gcToAnalytics(),
      affiliation: 'website',
      brand: 'Traeger',
      locale,
      currency,
    });
  }, [currency, locale]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        ...(form?.amount.includeCustomAmountOption && {
          customAmount: Yup.number()
            .min(form.amount.customAmountMin || 1, form.customAmount.errorMessage)
            .max(form.amount.customAmountMax || 5000, form.customAmount.errorMessage)
            .when('amount', (amount, schema) =>
              amount === 'custom' ? schema.required(form.customAmount.errorMessage) : schema,
            ),
        }),
        amount: Yup.string().required(form?.amount.errorMessage),
        senderName: Yup.string().required(form?.senderName.errorMessage),
        recipientName: Yup.string().required(form?.recipientName.errorMessage),
        recipientEmail: Yup.string()
          .email(form?.recipientEmail.errorMessage)
          .required(form?.recipientEmail.errorMessage),
        message: Yup.string().required(form?.message.errorMessage),
      }),
    [form],
  );

  const formik = useFormik({
    initialValues: {
      amount: form?.amount.options[0],
      customAmount: '',
      senderName: '',
      recipientName: '',
      recipientEmail: '',
      message: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await trigger({
          email: values.recipientEmail,
          toName: values.recipientName,
          fromName: values.senderName,
          message: values.message,
          amount: Number(values.amount === 'custom' ? values.customAmount : values.amount),
        });
        resetForm();
        setSubmissionError('');
      } catch {
        setSubmissionError(
          formSubmissionErrorText || 'Failed to add gift card. Please try again later.',
        );
      }
    },
  });

  return (
    <Container>
      {title && (
        <Typography align="center" component="h2" variant="h4" mb={3}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography align="center" variant="body1" pb={7}>
          {description}
        </Typography>
      )}
      {form && (
        <Form onSubmit={formik.handleSubmit}>
          {orderedFormKeys.map((_key: any) => {
            const key = _key as keyof FormProps;
            if (!form[key]) return null;
            const { label, includeCustomAmountOption, customAmountLabel, options } = form[
              key
            ] as any;
            const id = `GiftCardForm_${key}`;
            if (key === 'customAmount' && formik.values.amount !== 'custom') return null;
            return (
              <FormGroup key={key} sx={{ pb: 3 }}>
                <InputLabel
                  sx={{ fontSize: { xs: '14px' }, lineHeight: { xs: '1rem' }, fontWeight: 400 }}
                  htmlFor={`#${id}`}
                >
                  {label}
                </InputLabel>
                {options?.length ? (
                  <Select
                    name={key}
                    value={formik.values[key]}
                    onChange={formik.handleChange}
                    id={id}
                  >
                    {[...options, includeCustomAmountOption ? 'custom' : []].map((option) => {
                      return (
                        <MenuItem key={`${key}_${option}`} value={option}>
                          {option === 'custom'
                            ? customAmountLabel
                            : createCurrencyString(locale, currency || 'USD', Number(option), 0)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                ) : (
                  <TextField
                    type={formTypeOverrides[key as keyof typeof formTypeOverrides] || 'text'}
                    name={key}
                    value={formik.values[key]}
                    fullWidth
                    onChange={formik.handleChange}
                    id={id}
                    error={formik.touched[key] && Boolean(formik.errors[key])}
                    helperText={formik.touched[key] && formik.errors[key]}
                  />
                )}
              </FormGroup>
            );
          })}
          <BaseButtonWithProgress
            processing={formik.isSubmitting}
            type="submit"
            className="tw-w-full tw-mt-4"
            size="large"
          >
            {buttonText || 'Add To Cart'}
          </BaseButtonWithProgress>
          {submissionError && <FormError>{submissionError}</FormError>}
        </Form>
      )}
    </Container>
  );
};

export default GiftCardDetail;
